import React, {useEffect, useState, useCallback} from 'react';
import {
	Input,
	Typography,
	Tooltip,
	message,
	Pagination,
	Spin,
	Empty,
} from 'antd';
import AppHeader from '../../../components/Dashboard/AppHeader';
import {getCoursesDetailsAPI} from '../../../zustand/API';
import userStore from '../../../zustand/store/userStore';
import {debounce} from '../../../helper';

const {Title} = Typography;

const TeacherCourseDetails = () => {
	const {user} = userStore();
	const [lectures, setLectures] = useState([]);
	const [loading, setLoading] = useState(false);
	const [searchText, setSearchText] = useState('');
	const [pagination, setPagination] = useState({
		current: 1,
		pageSize: 6,
		total: 0,
	});

	const fetchLectures = async (page = 1, limit = 6, search = '') => {
		setLoading(true);
		try {
			const response = await getCoursesDetailsAPI({
				page,
				limit,
				email: user.email,
				subjectName: search,
				title: search.trim(),
			});

			if (response?.data?.lectures) {
				setLectures(response.data.lectures);
				setPagination({
					current: response.data.currentPage,
					pageSize: limit,
					total: response.data.totalLectures,
				});
			}
		} catch (err) {
			message.error('Failed to load lectures.');
		} finally {
			setLoading(false);
		}
	};

	const debouncedSearch = useCallback(
		debounce((value) => {
			fetchLectures(1, pagination.pageSize, value);
		}, 1000),
		[pagination.pageSize],
	);

	const handleSearchChange = (e) => {
		const value = e.target.value;
		setSearchText(value);
		if (value.trim() === '') {
			fetchLectures(1, pagination.pageSize, '');
		} else {
			debouncedSearch(value);
		}
	};

	const handlePageChange = (page, pageSize) => {
		setPagination((prev) => ({...prev, current: page, pageSize}));
		fetchLectures(page, pageSize, searchText);
	};

	useEffect(() => {
		fetchLectures();
	}, []);

	return (
		<div className="min-h-screen p-2">
			<AppHeader
				title="Lecture Resources"
				content="Access and manage your lecture PDF uploads for students."
			/>

			<div className="bg-white shadow-xl rounded-2xl p-6 mt-6">
				<div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6">
					<Title level={4} className="!mb-2 sm:!mb-0 !text-[#222]">
						Uploaded Lectures
					</Title>
					<Input
						placeholder="Search by subject or chapter"
						value={searchText}
						onChange={handleSearchChange}
						allowClear
						className="w-full sm:w-64 rounded-md"
					/>
				</div>
				{loading ? (
					<div className="text-center py-10 text-gray-500">
						<Spin />
					</div>
				) : lectures.length === 0 ? (
					<div className="text-center py-10">
						<Empty description="No lectures found." className="mt-10" />
					</div>
				) : (
					<>
						<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
							{lectures.map((lecture, index) => (
								<div
									key={lecture._id || index}
									className="bg-white border border-gray-200 rounded-xl shadow hover:shadow-lg transition-all duration-300 p-5"
								>
									<div className="flex items-center gap-3 mb-4">
										<img
											src={`https://api.dicebear.com/7.x/bottts-neutral/svg?seed=${lecture.title}`}
											alt="lecture avatar"
											className="w-10 h-10 rounded-full border"
										/>
										<div className="min-w-0">
											<p className="text-sm text-gray-500">Subject</p>
											<p className="text-[15px] font-medium text-gray-800 max-w-[180px] truncate cursor-pointer">
												{lecture.subject}
											</p>
										</div>
									</div>

									<div className="mb-2">
										<p className="text-xs text-gray-500">Chapter</p>
										<p className="text-sm font-medium text-gray-800">
											{lecture.title}
										</p>
									</div>

									<div className="mb-2">
										<p className="text-xs text-gray-500">Uploaded On</p>
										<p className="text-sm text-gray-700">
											{new Date(lecture.uploadedAt).toLocaleDateString()}
										</p>
									</div>

									<div className="text-right mt-4">
										<a
											href={lecture.fileUrl}
											target="_blank"
											rel="noreferrer"
											className="inline-block bg-blue-100 text-blue-600 text-xs font-semibold px-3 py-1 rounded-full hover:bg-blue-200 transition"
										>
											View PDF
										</a>
									</div>
								</div>
							))}
						</div>

						<div className="text-center">
							<Pagination
								current={pagination.current}
								pageSize={pagination.pageSize}
								total={pagination.total}
								onChange={handlePageChange}
								showSizeChanger={false}
							/>
						</div>
					</>
				)}
			</div>
		</div>
	);
};

export default TeacherCourseDetails;
